import { useEffect, useMemo, useRef, useState } from 'react';
import { line, scaleLinear } from 'd3';
import { cn } from '@playerbase/ui';
import type { FormFieldSummary, PlaytestEventTimeline, SentimentBand } from '@playerbase/types';

import {
  fetchPlaytestEventFieldSummary,
  fetchPlaytestEventTimeline,
} from '../../games/playtest-sessions-data.js';
import { useShell } from '../../shell/use-shell.js';
import { FieldSummaryList } from '../../feedback/field-summary-list.js';
import { SentimentDot } from '../../feedback/sentiment-dot.js';
import { usePlaytestEventLogTick } from './playtest-event-log-context.js';

// ---------------------------------------------------------------------------
// Playtest satisfaction timeline.
//
// A flow-level read-out shown above the session table: a satisfaction line
// across the flow's events (in order), one summary card per event, and a
// click-to-expand per-question breakdown reusing the shared field-summary
// list. Satisfaction is the mean form-level sentiment score (0-5) for the
// event across all the flow's testers; events not yet scored show as a gap on
// the line and "Not scored yet" on the card.
//
// At most five events are shown at once; longer flows reveal one more event
// per click (the line scrubs along with the cards). The chart maths use D3
// (scale + line generator); React renders every SVG element.
// ---------------------------------------------------------------------------

const WINDOW = 5;
const CHART_HEIGHT = 150;
const CHART_PAD_TOP = 14;
const CHART_PAD_BOTTOM = 14;
const CARD_GAP = 12; // matches the gap-3 on the card row, for dot alignment
const SCORE_MAX = 5;

interface PlaytestSatisfactionTimelineProps {
  playtestId: string;
}

export function PlaytestSatisfactionTimeline({
  playtestId,
}: PlaytestSatisfactionTimelineProps): JSX.Element | null {
  const { playtestSessionsVersion } = useShell();
  const eventLogTick = usePlaytestEventLogTick();

  const [events, setEvents] = useState<PlaytestEventTimeline[]>([]);
  const [loading, setLoading] = useState(true);
  const [windowStart, setWindowStart] = useState(0);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  // Per-event field summaries, lazily loaded on first card click. A cached
  // null means "loaded, nothing to show" so we don't refetch.
  const [summaries, setSummaries] = useState<Record<string, FormFieldSummary | null>>({});
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Reset all view state when the studio switches playtests.
  useEffect(() => {
    setWindowStart(0);
    setSelectedEventId(null);
    setSummaries({});
  }, [playtestId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const rows = await fetchPlaytestEventTimeline(playtestId);
      if (cancelled) return;
      setEvents(rows);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [playtestId, playtestSessionsVersion, eventLogTick]);

  // Lazy-load the selected event's per-question summary, caching the result.
  useEffect(() => {
    if (selectedEventId === null) return;
    if (selectedEventId in summaries) return;
    let cancelled = false;
    setSummaryLoading(true);
    void (async () => {
      const summary = await fetchPlaytestEventFieldSummary(selectedEventId);
      if (cancelled) return;
      setSummaries((prev) => ({ ...prev, [selectedEventId]: summary }));
      setSummaryLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEventId, summaries]);

  const totalResponses = useMemo(
    () => events.reduce((sum, e) => sum + e.responseCount, 0),
    [events],
  );

  // Nothing to aggregate yet: no events configured, or no responses on any
  // event. The empty session table below already conveys "no data".
  if (loading || events.length === 0 || totalResponses === 0) return null;

  const visible = events.slice(windowStart, windowStart + WINDOW);
  const hasPaging = events.length > WINDOW;
  const canPrev = windowStart > 0;
  const canNext = windowStart < events.length - WINDOW;

  const selectedEvent =
    selectedEventId === null ? null : (events.find((e) => e.eventId === selectedEventId) ?? null);
  const selectedSummary = selectedEventId === null ? null : (summaries[selectedEventId] ?? null);

  return (
    <section className="shrink-0 rounded-lg border border-border bg-surface-2 p-4 sm:p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
          Satisfaction timeline
        </span>
        {hasPaging && (
          <div className="flex items-center gap-2 text-xs text-text-muted">
            <span className="tabular-nums">
              {windowStart + 1}&ndash;{windowStart + visible.length} of {events.length}
            </span>
            <button
              type="button"
              aria-label="Previous event"
              disabled={!canPrev}
              onClick={() => setWindowStart((s) => Math.max(0, s - 1))}
              className="rounded-md border border-border px-2 py-1 text-text-muted enabled:hover:bg-surface-3 enabled:hover:text-text disabled:opacity-40"
            >
              &larr;
            </button>
            <button
              type="button"
              aria-label="Next event"
              disabled={!canNext}
              onClick={() => setWindowStart((s) => Math.min(events.length - WINDOW, s + 1))}
              className="rounded-md border border-border px-2 py-1 text-text-muted enabled:hover:bg-surface-3 enabled:hover:text-text disabled:opacity-40"
            >
              &rarr;
            </button>
          </div>
        )}
      </div>

      <SatisfactionChart events={visible} />

      <div className="mt-3 flex gap-3">
        {visible.map((event) => (
          <EventCard
            key={event.eventId}
            event={event}
            selected={event.eventId === selectedEventId}
            onSelect={() =>
              setSelectedEventId((current) => (current === event.eventId ? null : event.eventId))
            }
          />
        ))}
      </div>

      {selectedEvent !== null && (
        <div className="mt-4 rounded-md border border-border bg-surface p-4">
          <div className="mb-3 flex items-center gap-2">
            <span className="font-display text-sm font-semibold text-text">
              {selectedEvent.label}
            </span>
            <SentimentDot band={selectedEvent.band} showLabel />
          </div>
          {summaryLoading && !(selectedEvent.eventId in summaries) ? (
            <p className="text-sm text-text-muted">Loading responses…</p>
          ) : selectedSummary !== null && selectedSummary.fields.length > 0 ? (
            <FieldSummaryList fields={selectedSummary.fields} />
          ) : (
            <p className="text-sm text-text-muted">No responses to show for this event yet.</p>
          )}
        </div>
      )}
    </section>
  );
}

// --- Chart -----------------------------------------------------------------

// Theme-aware token references. Reading the CSS custom properties directly
// keeps the SVG fills correct in both themes without depending on whether a
// `fill-*` Tailwind utility happens to be generated.
function bandColorVar(band: SentimentBand | null): string {
  if (band === 'positive') return 'var(--pb-teal)';
  if (band === 'negative') return 'var(--pb-danger)';
  return 'var(--pb-text-muted)';
}

function SatisfactionChart({ events }: { events: PlaytestEventTimeline[] }): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (el === null) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const n = events.length;
  const height = CHART_HEIGHT;

  const yScale = useMemo(
    () =>
      scaleLinear()
        .domain([0, SCORE_MAX])
        .range([height - CHART_PAD_BOTTOM, CHART_PAD_TOP]),
    [height],
  );

  // Place each dot at the centre of the card beneath it: equal-width cards
  // with CARD_GAP between them, so dot i aligns with card i.
  const xAt = (i: number): number => {
    if (n <= 0 || width <= 0) return 0;
    const cardWidth = (width - CARD_GAP * (n - 1)) / n;
    return i * (cardWidth + CARD_GAP) + cardWidth / 2;
  };

  const points = events.map((event, i) => ({
    x: xAt(i),
    y: event.meanScore === null ? null : yScale(event.meanScore),
    band: event.band,
    scored: event.meanScore !== null,
  }));

  // One path with gaps over unscored events (defined() breaks the line so it
  // never draws straight through a missing point).
  const lineGen = line<(typeof points)[number]>()
    .defined((d) => d.y !== null)
    .x((d) => d.x)
    .y((d) => d.y as number);
  const linePath = width > 0 ? (lineGen(points) ?? '') : '';

  const gridScores = [0, 1, 2, 3, 4, 5];

  return (
    <div ref={containerRef} className="w-full">
      {width > 0 && (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label="Satisfaction across playtest events"
        >
          {gridScores.map((score) => {
            const y = yScale(score);
            return (
              <g key={score}>
                <line x1={0} x2={width} y1={y} y2={y} stroke="var(--pb-border)" strokeWidth={1} />
                <text
                  x={0}
                  y={y - 2}
                  fill="var(--pb-text-muted)"
                  fontSize={9}
                  className="font-mono"
                >
                  {score}
                </text>
              </g>
            );
          })}

          {linePath !== '' && (
            <path
              d={linePath}
              fill="none"
              stroke="var(--pb-text-muted)"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}

          {points.map((point, i) =>
            point.scored ? (
              <circle
                key={events[i]?.eventId ?? i}
                cx={point.x}
                cy={point.y as number}
                r={5}
                fill={bandColorVar(point.band)}
                stroke="var(--pb-surface-2)"
                strokeWidth={2}
              />
            ) : (
              // Unscored event: a hollow marker on the baseline keeps its
              // x-position visible without faking a score.
              <circle
                key={events[i]?.eventId ?? i}
                cx={point.x}
                cy={yScale(0)}
                r={4}
                fill="none"
                stroke="var(--pb-text-muted)"
                strokeWidth={1.5}
                strokeDasharray="2 2"
              />
            ),
          )}
        </svg>
      )}
    </div>
  );
}

// --- Card ------------------------------------------------------------------

function EventCard({
  event,
  selected,
  onSelect,
}: {
  event: PlaytestEventTimeline;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  const scored = event.meanScore !== null;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'flex flex-1 flex-col gap-1 rounded-md border bg-surface p-3 text-left transition-colors',
        selected
          ? 'border-accent bg-accent/10'
          : 'border-border hover:border-border-strong hover:bg-surface-3',
      )}
    >
      <span
        className="font-display text-2xl font-bold tabular-nums"
        style={{
          color: scored ? bandColorVar(event.band) : 'var(--pb-text-muted)',
        }}
      >
        {scored ? (event.meanScore as number).toFixed(1) : '—'}
      </span>
      <span className="truncate text-sm font-medium text-text" title={event.label}>
        {event.label}
      </span>
      <span className="text-xs text-text-muted">
        {event.responseCount} {event.responseCount === 1 ? 'response' : 'responses'}
      </span>
      {scored ? (
        <SentimentDot band={event.band} showLabel />
      ) : (
        <span className="text-xs text-text-muted">Not scored yet</span>
      )}
    </button>
  );
}
